import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

// Re-authored from ../design-system/components/navigation/Tabs.jsx (ADR-032:
// match visual output, not the prototype's injected-CSS/UMD code).
//
// Underline indicator: uses `after:` Tailwind utilities on each tab button.
// The gold underline is a pseudo-element (after:) that scales from 0 → 1 when
// data-active="true". This avoids injected CSS while exactly matching the DS
// scaleX(0→1) animation.

export interface TabItem {
  value: string;
  label: ReactNode;
  icon?: ReactNode;
  count?: number;
}

export interface TabsProps {
  tabs: TabItem[];
  /** Controlled active value */
  value?: string;
  /** Uncontrolled default; falls back to tabs[0].value */
  defaultValue?: string;
  onChange?: (value: string) => void;
  variant?: 'underline' | 'pill';
  className?: string;
}

export function Tabs({
  tabs,
  value,
  defaultValue,
  onChange,
  variant = 'underline',
  className,
}: TabsProps) {
  const [internal, setInternal] = useState<string>(
    defaultValue ?? tabs[0]?.value ?? '',
  );

  // Controlled when `value` is provided; uncontrolled otherwise.
  const active = value !== undefined ? value : internal;

  const select = (v: string) => {
    if (value === undefined) {
      setInternal(v);
    }
    onChange?.(v);
  };

  // Roving-tabindex keyboard nav (WAI-ARIA tablist): arrows/Home/End move focus
  // between tabs; Enter/Space activate natively via the button's onClick. Manual
  // activation (focus ≠ select) avoids firing onChange on every arrow press —
  // important when a tab drives an expensive query.
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, idx: number) => {
    let next: number;
    switch (e.key) {
      case 'ArrowRight':
        next = (idx + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (idx - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    tabRefs.current[next]?.focus();
  };

  const isPill = variant === 'pill';

  return (
    <div
      role="tablist"
      className={cn(
        'flex',
        isPill
          ? 'items-center gap-1 rounded-full border border-[var(--border-hairline)] bg-input p-1'
          : 'items-end gap-5 border-b border-[var(--border-soft)]',
        className,
      )}
    >
      {tabs.map((t, i) => {
        const isActive = active === t.value;
        return (
          <button
            key={t.value}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            data-active={isActive ? 'true' : undefined}
            onClick={() => select(t.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={cn(
              'relative inline-flex items-center gap-1.5 text-sm font-semibold transition-colors',
              'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-glow)] focus-visible:rounded-[2px]',
              isPill
                ? cn(
                    'rounded-full px-4 py-2',
                    isActive
                      ? 'bg-card text-[var(--ollio-navy)] shadow-[var(--shadow-sm)]'
                      : 'text-muted hover:text-[var(--text-heading)]',
                  )
                : cn(
                    // Underline variant: gold indicator via after: pseudo
                    'pb-2.5',
                    'after:absolute after:left-0 after:right-0 after:-bottom-px after:h-0.5',
                    'after:bg-[var(--ollio-gold)] after:scale-x-0 after:transition-transform',
                    'data-[active=true]:after:scale-x-100',
                    isActive
                      ? 'text-[var(--ollio-navy)]'
                      : 'text-muted hover:text-[var(--text-heading)]',
                  ),
            )}
          >
            {t.icon && (
              <span className="flex-none" aria-hidden="true">
                {t.icon}
              </span>
            )}
            {t.label}
            {t.count !== undefined && (
              <span
                className={cn(
                  'rounded-full px-[7px] text-[0.625rem] font-semibold leading-5',
                  isPill && isActive
                    ? 'bg-[var(--ollio-navy-light)] text-[var(--ollio-navy)]'
                    : 'bg-[var(--ollio-cream-200)] text-muted',
                )}
              >
                {t.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
